import { ChunkHandler } from './ChunkHander';
import { getHiDpiRatio } from '../../../../render-settings';

const MAX_CHUNK_SIZE = 2048;
// Frame is a cheap prefeching mechanism; it's used to request tiles outside the visible area so there's less
// blurring while panning. Obviously more tiles means more requests and our request queue isn't good enough to manage
// them yet, so setting frame to 0 unless needed for rotation.
const FRAME_SIZE_NORMAL = 0;
const FRAME_SIZE_ROTATED = 2;

export class TileLayer {
  pyramidZoom;
  viewportSize = { width: 0, height: 0 };

  constructor (pyramidLevel, viewport, requestQueue) {
    this.pyramidLevel = pyramidLevel;
    this.pyramidZoom = pyramidLevel.zoom;

    this.viewport = viewport;
    this.requestQueue = requestQueue;

    const xInfo = viewport.getXinfoObj();

    this.visible = false;
    this.xTileSize = {
      width: xInfo.tileWidth,
      height: xInfo.tileHeight
    };
  }

  attachRenderer (renderer) {
    this.renderer = renderer;

    this.chunkHandler = new ChunkHandler(renderer, this.pyramidZoom);

    const chunkWidth = getChunkSize(this.xTileSize.width);
    const chunkHeight = getChunkSize(this.xTileSize.height);

    const chunksX = Math.ceil(this.pyramidLevel.size.w / chunkWidth);
    const chunksY = Math.ceil(this.pyramidLevel.size.h / chunkHeight);

    for (let x = 0; x < chunksX; x++) {
      for (let y = 0; y < chunksY; y++) {
        this.chunkHandler.addChunk(x, y, chunkWidth, chunkHeight, this.xTileSize.width, this.xTileSize.height);
      }
    }
  }

  resize (width, height) {
    this.viewportSize = { width, height };
  }

  update (state, isActiveLayer, cameraRect) {
    const currentZoom = state.zoom / getHiDpiRatio();
    const scale = this.pyramidZoom / (currentZoom);

    if (isActiveLayer) {
      // Compute the new size of tiles
      const scaledTileWidth = this.xTileSize.width * scale;
      const scaledTileHeight = this.xTileSize.height * scale;

      // Compute which tiles are now visible (including an outer frame)
      const frameSize = state.angleDeg % 180 === 0 ? FRAME_SIZE_NORMAL : FRAME_SIZE_ROTATED;
      const tileL = Math.max(0, Math.floor(state.left / currentZoom / scaledTileWidth) - (frameSize * 2));
      const tileR = Math.ceil(tileL + (this.viewportSize.width * getHiDpiRatio()) / scaledTileWidth) + ((frameSize * 4) + getHiDpiRatio());
      const tileT = Math.max(0, Math.floor(state.top / currentZoom / scaledTileHeight) - (frameSize * 2));
      const tileB = Math.ceil(tileT + (this.viewportSize.height * getHiDpiRatio()) / scaledTileHeight) + ((frameSize * 4) + getHiDpiRatio());

      // Show or create tiles that are in view
      for (let tileX = tileL; tileX <= tileR; tileX++) {
        for (let tileY = tileT; tileY <= tileB; tileY++) {
          this.chunkHandler.createTile(tileX, tileY, tileX * this.xTileSize.width, tileY * this.xTileSize.height, this.requestQueue, this.pyramidZoom);
        }
      }
    }

    this.chunkHandler.update(isActiveLayer, scale, cameraRect);
  }

  draw () {
    this.chunkHandler.draw();
  }
}

function getChunkSize (tileSize) {
  return Math.floor(MAX_CHUNK_SIZE / tileSize) * tileSize;
}
